import { JYOTISHA_TOPIC_SEARCH_INDEX } from '../data/jyotishaTopicSearchIndex';
import { isQuizExamTopic } from '../data/jyotishaQuizConstants';

const LAST_TOPIC_STORAGE_KEY = 'richuals.jyotisha.lastTopicId';

/** Fisher–Yates shuffle (mutates copy). */
function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

/**
 * @param {number} count
 * @param {string[]} [excludeIds]
 */
export function pickRandomLearnTopics(count = 2, excludeIds = []) {
  const exclude = new Set(excludeIds);
  const pool = JYOTISHA_TOPIC_SEARCH_INDEX.filter(
    (e) => e.id && !exclude.has(e.id) && !isQuizExamTopic(e.id),
  );
  if (pool.length <= count) return shuffle(pool);
  return shuffle(pool).slice(0, count);
}

/** @returns {typeof JYOTISHA_TOPIC_SEARCH_INDEX} */
export function getTopicCloudEntries() {
  return JYOTISHA_TOPIC_SEARCH_INDEX;
}

/**
 * Weight 1–5 for cloud tag size (stable per id).
 * @param {string} id
 */
export function topicCloudWeight(id) {
  let h = 0;
  for (let i = 0; i < id.length; i += 1) h = (h * 31 + id.charCodeAt(i)) % 997;
  return 1 + (h % 5);
}

/** @typedef {'xs' | 'sm' | 'md' | 'lg' | 'xl'} TopicCloudSizeTier */

/**
 * @param {number} weight 1–5
 * @returns {TopicCloudSizeTier}
 */
export function topicCloudSizeTier(weight) {
  if (weight >= 5) return 'xl';
  if (weight >= 4) return 'lg';
  if (weight >= 3) return 'md';
  if (weight >= 2) return 'sm';
  return 'xs';
}

/**
 * Ordered "balls" layout: staggered rows, one after another (percent).
 * @param {string} id
 * @param {number} index
 * @param {number} total
 */
export function topicCloudScatterPosition(id, index, total) {
  const cols = Math.max(4, Math.ceil(Math.sqrt(Math.max(total, 1) * 1.05)));
  const rows = Math.max(1, Math.ceil(total / cols));
  const row = Math.floor(index / cols);
  const col = index % cols;

  // Alternate row offset to make a "packed balls" look.
  const stagger = row % 2 === 0 ? 0 : (100 / cols) * 0.5;
  const left = ((col + 0.5) / cols) * 100 + stagger;
  const top = ((row + 0.5) / rows) * 100;

  return {
    left: Math.min(96, Math.max(4, left)),
    top: Math.min(95, Math.max(5, top)),
  };
}

/**
 * @param {TopicCloudSizeTier} tier
 * @param {boolean} isDark
 */
export function topicCloudBubbleClasses(tier, isDark) {
  const shell = isDark
    ? 'border-amber-300/70 bg-transparent text-amber-100 shadow-[0_0_0_1px_rgba(251,191,36,0.14)] hover:border-amber-200 hover:bg-amber-400 hover:text-black'
    : 'border-black/70 bg-transparent text-slate-900 shadow-[0_0_0_1px_rgba(0,0,0,0.12)] hover:border-black hover:bg-amber-400 hover:text-black';

  const sizes = {
    xs: 'w-[6.5rem] h-[6.5rem] sm:w-[7.2rem] sm:h-[7.2rem] text-[11px] sm:text-xs',
    sm: 'w-[6.5rem] h-[6.5rem] sm:w-[7.2rem] sm:h-[7.2rem] text-[11px] sm:text-xs',
    md: 'w-[6.5rem] h-[6.5rem] sm:w-[7.2rem] sm:h-[7.2rem] text-[11px] sm:text-xs',
    lg: 'w-[6.5rem] h-[6.5rem] sm:w-[7.2rem] sm:h-[7.2rem] text-[11px] sm:text-xs',
    xl: 'w-[6.5rem] h-[6.5rem] sm:w-[7.2rem] sm:h-[7.2rem] text-[11px] sm:text-xs',
  };

  return {
    bubble: `rounded-full border flex flex-col items-center justify-center p-2 text-center leading-tight transition-transform duration-300 ease-[cubic-bezier(0.2,1.35,0.45,1)] hover:-translate-y-1 hover:scale-[1.12] active:scale-[0.94] focus:outline-none focus-visible:ring-2 focus-visible:ring-fuchsia-400/80 ${shell} ${sizes[tier]}`,
    emoji: '',
  };
}

/** @param {string | null} topicId */
export function saveLastJyotishaTopic(topicId) {
  if (!topicId || typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(LAST_TOPIC_STORAGE_KEY, topicId);
  } catch {
    /* quota / private mode */
  }
}

/** @returns {string | null} */
export function readLastJyotishaTopic() {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(LAST_TOPIC_STORAGE_KEY);
  } catch {
    return null;
  }
}
